import { getCodeFromErrors, getExtensionFromErrors } from '@common/components';
import type { ApolloError } from '@common/hooks';

// Copy for a shift that left the approvable set, keyed by the raw `currentState` the backend names on
// its SHIFT_NOT_APPROVABLE signal (Phase 2). Each string names the state only — never the backend
// message, which can carry internal IDs. An absent/unknown state falls back to NOT_APPROVABLE_FALLBACK.
const NOT_APPROVABLE_COPY: Record<string, string> = {
  confirmed: 'This shift has already been approved.',
  cancelled: 'This shift has been cancelled and can no longer be approved.',
  rejected: 'This shift was rejected and can no longer be approved.',
  retracted:
    'This shift was retracted by the worker and can no longer be approved.',
};

const NOT_APPROVABLE_FALLBACK =
  'This shift can no longer be approved. Refresh to see its latest status.';

const GENERIC_ERROR = 'Failed to change works shift state. Please try again.';

// Maps a performWorksShiftAction failure to the toast the Approve button raises. Consumes the
// Phase-2 SHIFT_NOT_APPROVABLE signal: a stale approve (the shift already left the approvable set)
// is a warning naming the current state, not a generic error — everything else keeps the generic
// error copy. The `currentState` extension names why; an absent/unknown state falls back.
export function performWorksShiftActionErrorMessage(error: ApolloError): {
  kind: 'warning' | 'error';
  message: string;
} {
  if (getCodeFromErrors(error.graphQLErrors) === 'SHIFT_NOT_APPROVABLE') {
    const state = getExtensionFromErrors(error.graphQLErrors, 'currentState');
    return {
      kind: 'warning',
      message: (state && NOT_APPROVABLE_COPY[state]) || NOT_APPROVABLE_FALLBACK,
    };
  }
  return { kind: 'error', message: GENERIC_ERROR };
}
